<template>
  <SelectGrid
    ref="gridRef"
    selection-mode="single"
    :page-size="5"
    :grid-height="350"
    :disabled="tipoReadOnly"
    :dropdown-width="800"
    :placeholder="placeholderText"
    value-expr="Id"
    :display-expr="displayExpr"
    key-api="Id"
    :load-data="loadData"
    :columns="columns"
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
  />
</template>

<script setup lang="ts">
import SelectGrid from "@/.shared/components/SelectGrid.vue";
import { computed, ref } from "vue";
import { useIntl } from "@services/i18n";
import { useApi } from "@api/integration";
import type { CreateTicket } from "@/.shared/components/soporte/models/CreateTicket";
import type { DXLoadOptions } from "@api/api";

const props = defineProps<{
  variant: "colaborador" | "inmobiliaria";
  model: CreateTicket;
  tipoReadOnly: boolean;
  isCreatorSelectable: boolean;
  modelValue: string[];
}>();

defineEmits<{
  (e: "update:modelValue", v: string[]): void;
}>();

const api = useApi();
const intl = useIntl();
const gridRef = ref<InstanceType<typeof SelectGrid>>();

const placeholderText = computed(() =>
  props.variant === "colaborador" ? "Seleccione una operación" : "Seleccione un trámite"
);

const columns = computed(() =>
  props.variant === "colaborador"
    ? [
        { dataField: "CodigoSecuencial", caption: "ID", format: "0000000000" },
        { dataField: "DocumentoTitular", caption: "DNI" },
        { dataField: "CupsLuz", caption: "CUPS Luz" },
        { dataField: "CupsGas", caption: "CUPS Gas" }
      ]
    : [
        {
          dataField: "TipoSuministro",
          caption: "Suministro",
          template: "serviciosTramiteTemplate"
        },
        { dataField: "DocumentoTitular", caption: "Documento" },
        { dataField: "DireccionInmueble", caption: "Dirección", minWidth: 400 }
      ]
);

const displayExpr = computed(() =>
  props.variant === "colaborador" ? displayColaborador : displayInmobiliaria
);

function displayColaborador(data: any) {
  return `${data.CodigoSecuencial.toString().padStart(10, "0")}`;
}

function displayInmobiliaria(data: any) {
  let tipoSuministro = data.TipoSuministro[0];
  if (tipoSuministro === "LUZ" || tipoSuministro === "GAS") {
    tipoSuministro = "ENERGIA";
  }
  return `${intl.i18n.t(`operaciones.tipo_suministro.${tipoSuministro}`)} - ${data.DocumentoTitular} - ${data.DireccionInmueble}`;
}

const loadData = async (options: DXLoadOptions) => {
  const q = options as DXLoadOptions;
  if (props.isCreatorSelectable) {
    q.filter = [["IdUsuario", "=", props.model?.IdUsuario]];
  }
  if (props.variant === "colaborador") {
    return await api.callStandardApi("getOperacionesEnergiaForGrid", { showLoading: false }, q);
  }
  return await api.callStandardApi(
    "getTramitesForGridSoporte",
    { showLoading: false },
    props.model?.IdUsuario ?? undefined,
    q
  );
};

defineExpose({
  clearSelection: () => gridRef.value?.clearSelection(),
  refreshGrid: () => gridRef.value?.refreshGrid(),
  getSelectedRowsData: () => gridRef.value?.getSelectedRowsData() ?? Promise.resolve([])
});
</script>
